/**
 * Opening a table.
 *
 * This screen does one thing and then gets out of the way: it creates the room
 * and sends the host into it. Everything a host might want to decide — seats,
 * the clock, whether to keep dealing, whether anybody may watch — is decided in
 * the lobby instead, alongside the room code people are already joining with.
 *
 * That ordering is the point. Handing somebody a code they can share takes one
 * tap, and the settings are there for as long as the room sits in its lobby
 * rather than being a form standing between the host and the thing they asked
 * for. It also means the host is *in* the room while they change its settings,
 * which is the only way to see what those settings did.
 *
 * The room is created at the game's minimum size. The host raises it from the
 * lobby, and the server caps that at what their entitlements bought.
 */

import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { createRoom } from '../net/session.js';
import { useSession } from '../session-context.js';
import { useCatalog } from '../use-catalog.js';
import { describeError } from './errors.js';

export function HostGame() {
  const { gameId } = useParams<{ gameId: string }>();
  const { session, config } = useSession();
  const { catalog } = useCatalog();
  const navigate = useNavigate();

  const [error, setError] = useState<string | null>(null);

  /*
   * Opening a table is not idempotent, and this effect runs twice in
   * development.
   *
   * React's StrictMode deliberately mounts, unmounts and remounts every
   * component once, which for an effect that CREATES something means two of
   * them — two rooms, two codes, and the one the host was shown abandoned in a
   * lobby. A ref is the guard because it survives the remount that a piece of
   * state would not.
   */
  const opened = useRef(false);

  useEffect(() => {
    if (!session || !gameId || opened.current) return;
    if (!catalog) return;

    const game = catalog.games.find((candidate) => candidate.id === gameId);
    if (!game) {
      setError('No such game.');
      return;
    }
    if (!game.owned || game.maxSeats < game.minPlayers) {
      setError(`You cannot open a table for ${game.name} yet.`);
      return;
    }

    opened.current = true;

    void (async () => {
      try {
        const room = await createRoom(session, gameId, { seatCount: game.minPlayers });
        /*
         * Via the name-and-face screen, not straight into the room.
         *
         * The host is the one person who never saw it, so they arrived at
         * their own table with no name they had chosen and the card-back
         * avatar. The match reads a profile when a presence JOINS and caches
         * it, so this has to happen before the room is entered - setting it
         * afterwards would not reach the table until the next join.
         *
         * `replace`, so Back goes to the game rather than opening a second
         * room, and a reload of the room does not re-run this screen.
         */
        const solo = game.maxPlayers === 1;

        /*
         * A patience does not ask you who you are.
         *
         * A name and a face are how other people know which player is you, and
         * a game for one has no other people - so the screen asking for them
         * stood between somebody and a deal for no benefit they would ever
         * see. Skipped once they HAVE a profile, which is the only thing the
         * table needs from them.
         *
         * Not skipped when they have none. Somebody whose first ever game is
         * Klondike would otherwise be given a name they never chose and meet
         * it later at a table full of people - so they are asked once, and
         * every multiplayer table goes on asking, which is where changing it
         * matters anyway.
         */
        const known =
          (config?.profile?.displayName ?? '').trim() !== '' &&
          (config?.profile?.avatarId ?? '') !== '';

        if (solo && known) {
          navigate(`/play/${room.roomCode}`, { replace: true });
          return;
        }

        /*
         * `solo` travels with `hosting` because the next screen cannot work
         * it out. It has a room code and nothing else - no game, no seat
         * range - so without being told it greets a patience the same way it
         * greets a table of four, and offers to help you invite people to a
         * game for one.
         */
        navigate(`/join/${room.roomCode}`, {
          replace: true,
          state: { hosting: true, solo },
        });
      } catch (caught) {
        opened.current = false;
        setError(describeError(caught, 'That table could not be opened.'));
      }
    })();
  }, [session, gameId, catalog, config, navigate]);

  if (error) {
    return (
      <div className="page narrow">
        <h1 className="page-title">Can&apos;t open a table</h1>
        <p className="banner error">{error}</p>
        <Link className="big-button primary" to="/library">
          Your games
        </Link>
      </div>
    );
  }

  return <div className="splash">Setting out the chairs…</div>;
}
